import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import styled from "styled-components";
import InputGroup from 'react-bootstrap/InputGroup';
import Col from 'react-bootstrap/Col';
import { ExButton } from "../ExButton";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { ExFormLabel, FormFeedback } from "../ExControls";
import { ButtonCopy, cnpjIsValid, cpfIsValid, emailIsValid } from "../../commons/utils";
import "./styles.css";
import moment from "moment";
import { Eye, EyeOff, Search } from "lucide-react";

const VbTextFixed = styled(InputGroup.Text)`
`

interface InputProps {
    id: any;
    type: any;
    label?: String;
    placeholder?: any;
    defaultValue?: any;
    feedback?: String;
    textfixed?: String;
    required?: any;
    md?: String;
    ref?: any;
    size?: any;
    onAfterChange?: any;
    search?: any;
    disabled?: any;
    model?: any;
    validated?: any;
    autoFocus?: any;
    maxLength?: any;
    textarea?: any;
    rows?: any;
    noDebounce?: boolean
    copy?: any;
}

export const Input: React.FC<InputProps> = forwardRef((props: InputProps, ref: any) => {
    const inputRef = useRef<any>();
    const [value, setValue] = useState<any>(props.defaultValue ? (props.type === 'date' ? moment(props.defaultValue).format('YYYY-MM-DD') : props.defaultValue) : '');
    const [showPassword, setShowPassword] = useState<any>();
    const [error, setError] = useState<any>(false);
    const [label, setLabel] = useState<any>(props.label);
    const [model, setModel] = useState<any>(props.model);
    const [msgInfo, setMsgInfo] = useState<any>('');

    const debounceTimeoutRef = useRef<any>(null); // ref para o debounce

    function getValue() {
        if ((props.type === 'number') || (props.type === 'date')) {
            if (!value)
                return null;

            return value;
        }
        else
            return value?.toString().trim().replace(/[\r\n]+/g, ' ');
    }

    function getId() {
        return props.id;
    }

    function validate(_value?: any) {
        _value = _value ? _value : value;

        var result = props.required && !props.disabled ? (_value !== '') : true;

        if (model && (!props.disabled && (props.required || (_value !== '')))) {
            if (model === 'phone')
                result = _value.length >= 14;
            else if (model === 'cpf')
                result = cpfIsValid(_value)
            else if (model === 'cnpj')
                result = cnpjIsValid(_value)
            else if (model === 'email')
                result = emailIsValid(_value);

            inputRef.current.setCustomValidity(result ? '' : 'Invalid field')
        }

        return result
    }

    useImperativeHandle(ref, () => ({
        getValue,
        setValue,
        setError,
        setLabel,
        setModel,
        setMsgInfo,
        getId,
        validate
    }));

    function applyCustomMask(_value: string, mask: any): string {
        if (!mask)
            return _value;

        let result = '';
        let maskIndex = 0;

        for (let i = 0; i < _value.length; i++) {
            const char = _value[i];

            if (maskIndex >= mask.length) {
                break;
            }

            if (/\d/.test(char)) {
                while (maskIndex < mask.length && (mask[maskIndex] === '(' || mask[maskIndex] === ')' || mask[maskIndex] === ' ' || mask[maskIndex] === '-' || mask[maskIndex] === '/' || mask[maskIndex] === '.')) {
                    result += mask[maskIndex];
                    maskIndex++;
                }

                if (mask[maskIndex] === '9') {
                    result += char;
                    maskIndex++;
                }
            } else if (mask[maskIndex] === char) {
                result += char;
                maskIndex++;
            }
        }

        return result;
    }

    function validateModel() {
        if ((model === 'phone') && (value.length < 14))
            return 'Telefone inválido'
        else if ((model === 'cpf') && (!cpfIsValid(value)))
            return 'CPF inválido'
        else if ((model === 'cnpj') && (!cnpjIsValid(value)))
            return 'CNPJ inválido'
        else if ((model === 'email') && (!emailIsValid(value)))
            return 'E-mail inválido'
    }

    function getModel(_value: any) {
        if (model) {
            if (model === 'phone') {
                if (_value.length <= 14)
                    return '(99) 9999-9999'
                else
                    return '(99) 99999-9999'
            }
            else if (model === 'cpf')
                return '999.999.999-99'
            else if (model === 'cnpj')
                return '99.999.999/9999-99'
            else if (model === 'cep')
                return '99999-999'
            else if (model === 'cep-number')
                return '99999999'
        } else
            return ''
    }

    function onChange(e: any) {
        const _value = e.target.value;
        const _maskedValue = applyCustomMask(_value, getModel(_value));

        setValue(_maskedValue);

        if (props.noDebounce) {
            props.onAfterChange?.(_maskedValue);
            validate(_value)
            return
        }

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            if (props.onAfterChange) {
                props.onAfterChange(_maskedValue);
            }
        }, 300);

        validate(_value);
    }

    return (
        <Form.Group as={Col} md={props?.md ? props.md : "12"} controlId={props.id}>
            {label && <ExFormLabel>{(props?.required ? '*' : '') + label}</ExFormLabel>}
            <InputGroup size={props.size ? props.size : "sm"} hasValidation>
                <Form.Control
                    ref={inputRef}
                    as={props.textarea && 'textarea'}
                    name={props.id}
                    type={props.type !== 'password' ? props.type : showPassword ? 'text' : 'password'}
                    placeholder={props.placeholder}
                    onChange={onChange}
                    required={props.required}
                    isInvalid={error}
                    autoComplete="off"
                    disabled={props.disabled}
                    value={value}
                    autoFocus={props.autoFocus}
                    style={{ fontSize: '14px' }}
                    rows={props.rows}
                    maxLength={props.maxLength}
                    autoCapitalize={model === 'email' ? 'none' : 'sentences'}
                />

                {props.copy && (
                    <InputGroup.Text style={{ padding: 0, background: 'transparent', border: 'none' }}>
                        <ButtonCopy text={getValue()} />
                    </InputGroup.Text>
                )}

                {props.search &&
                    <InputGroupText>
                        <Search size={20} />
                    </InputGroupText>
                }

                {props.type === 'password' &&
                    <ExButton
                        size={props.size ? props.size : "sm"}
                        style={{
                            display: 'flex',
                            borderTopRightRadius: '4px',
                            borderBottomRightRadius: '4px',
                            fontSize: '0.875rem',
                        }}
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
                    </ExButton>
                }

                <FormFeedback type="invalid">
                    {
                        (model && props.validated && value) ? validateModel() :
                            !error &&
                            (props.feedback ? props.feedback : props.required ? 'Campo obrigatório.' : '')
                    }
                </FormFeedback>

                {props.textfixed &&
                    <VbTextFixed id="basic-addon2">
                        {props.textfixed}
                    </VbTextFixed>}
            </InputGroup>
            {msgInfo && <div style={{ fontSize: '12px' }}>{msgInfo}</div>}
        </Form.Group >
    );
});
